import React, { useEffect, useState } from 'react';

import { NamedLink } from '../../components';
import { listingPageLinkProps } from '../../util/listingSeoUrl';
import { packageSuggestions } from '../../util/api';

import css from './ReadyPackagesSection.module.css';

/**
 * "Färdiga paket för din bil" (P25009-402 BR-2/BR-3).
 *
 * After a plate lookup we know the buyer's car, and the catalogue may already
 * hold complete sets that fit it. Nothing joined the two: the results grid
 * showed loose rims and tyres, and a ready package sat unseen.
 *
 * For a staggered car this is the only thing that CAN be shown. Its two axles
 * take different sizes, so no single listing is a complete answer — only a
 * package carries both, which is what P25009-399 made possible.
 *
 * Renders nothing at all when there is no car, or when nothing fits. An empty
 * heading would be worse than silence: it tells the buyer their car is not
 * catered for, which is not what "we have no package right now" means.
 */

const fmt = n => Math.round(Number(n) || 0).toLocaleString('sv-SE');

const TYPE_LABELS = { wheel: 'Komplett hjulsats', tire: 'Däckpaket', rim: 'Fälgpaket' };

/* The suggestion is a slim shape, not a listing entity, so it is put back into
 * the shape listingPageLinkProps reads. That keeps the breadcrumb URL rules
 * (P25009-244) in the one place that owns them, and falls back to the legacy
 * /l/:slug/:id route by itself when a package has no category to build from. */
const asListingEntity = suggestion => ({
  id: { uuid: suggestion.listingId },
  attributes: {
    title: suggestion.title,
    publicData: {
      categoryLevel1: suggestion.categoryLevel1,
      categoryLevel2: suggestion.categoryLevel2,
      adType: suggestion.adType,
      listingType: suggestion.listingType,
    },
  },
});

const PackageCard = ({ suggestion }) => {
  const image = (suggestion.imageUrls || [])[0];
  const perUnit =
    suggestion.setSize > 0 ? Math.round(suggestion.price / suggestion.setSize) : 0;
  const link = listingPageLinkProps(asListingEntity(suggestion), {
    id: suggestion.listingId,
    slug: 'paket',
  });

  return (
    <NamedLink className={css.card} name={link.name} params={link.params}>
      <div className={css.imageWrap}>
        {image ? (
          <img className={css.image} src={image} alt={suggestion.title} loading="lazy" />
        ) : (
          <span className={css.imagePlaceholder} aria-hidden="true" />
        )}
        {/* BR-3 — a buyer looking at a staggered car needs to see at a glance
          * that this set answers both axles, because that is the thing no
          * ordinary annons can do. */}
        {suggestion.isStaggered ? <span className={css.staggeredBadge}>Fram + bak</span> : null}
      </div>

      <div className={css.body}>
        <div className={css.type}>{TYPE_LABELS[suggestion.packageType] || 'Paket'}</div>
        <div className={css.title}>{suggestion.title}</div>
        {suggestion.combination?.length ? (
          <div className={css.sizes}>{suggestion.combination.join(' + ')}</div>
        ) : null}
        {/* A package is offered because it FITS, not because it was built for
          * this car, and its title names the car it was built for. Saying so is
          * the difference between a useful suggestion and one that looks like a
          * mistake — a buyer with a Peugeot should not have to guess why they
          * are being shown "Komplett hjulsats Volvo V60". */}
        {!suggestion.builtForThisCar && suggestion.fitmentLabel ? (
          <div className={css.fitNote}>Byggd för {suggestion.fitmentLabel} · passar din bil</div>
        ) : null}
        <div className={css.price}>
          {fmt(suggestion.price)} kr
          {perUnit > 0 ? <span className={css.perUnit}> · {fmt(perUnit)} kr/st</span> : null}
        </div>
      </div>
    </NamedLink>
  );
};

const ReadyPackagesSection = ({ car }) => {
  const [suggestions, setSuggestions] = useState([]);

  const make = car?.make || '';
  const model = car?.model || '';
  /* P25009-489 BR-3 — the looked-up car's minimum wheel size. The service
   * matches any generation of the model, and an older one may run a size the
   * buyer's car cannot take (an XC40 runs 17", an EX40 needs 19"). */
  const minDiameter = Number(car?.minDiameter) || null;

  useEffect(() => {
    if (!make || !model) {
      setSuggestions([]);
      return undefined;
    }

    /* The car can change while a request is in flight — a buyer looking up a
     * second plate. Without this guard the slower answer wins and the section
     * shows packages for the previous car. */
    let current = true;
    packageSuggestions({ make, model, limit: 6, ...(minDiameter ? { minDiameter } : {}) })
      .then(res => {
        if (!current) return;
        setSuggestions(res.suggestions || []);
      })
      // Silent: this is an extra on top of the results, never the search itself.
      .catch(() => current && setSuggestions([]));

    return () => {
      current = false;
    };
  }, [make, model, minDiameter]);

  if (!suggestions.length) return null;

  /* Said of the packages actually listed, not of the car. The service reports a
   * model as staggered when ANY of its generations is, which is true and useful
   * for matching but wrong as a description of what is on screen — it claimed
   * "these cover both axles" above four-of-one-size sets. */
  const anyStaggered = suggestions.some(suggestion => suggestion.isStaggered);

  return (
    <section className={css.section}>
      <div className={css.head}>
        <h2 className={css.heading}>Färdiga paket för din bil</h2>
        <p className={css.sub}>
          {anyStaggered
            ? 'Din bil tar olika dimension fram och bak. Paketen nedan täcker båda axlarna — köp som en vara.'
            : 'Kompletta satser som passar din bil, köpta som en vara.'}
        </p>
      </div>
      <div className={css.grid}>
        {suggestions.map(suggestion => (
          <PackageCard key={suggestion.listingId} suggestion={suggestion} />
        ))}
      </div>
    </section>
  );
};

export default ReadyPackagesSection;
